import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography,
  Grid2,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { ReactComponent as CloseIcon } from '../../../assets/SVG/close-circle-greay.svg';
import {
  autocompleteSx,
  Clinic,
  filterInputSubLabelSx,
  mapUserClinicsResponse,
  modalCloseIconSx,
  modalCloseSx,
  modalHeaderSx,
  modalSaveSx,
  UserClinicDepartments,
} from '../../Inventory/Common/inventory.helper';
import {
  ApprovalFilterValues,
  APPROVAL_FILTER_INITIAL_VALUES,
} from './inventoryApproval.types';
import { filterModalContainerSx } from './InventoryApprovalSx';
import { STATUS_OPTIONS } from './ApprovalDummyData';
import { useDocumentTypes } from './inventoryApproval.queries';
import { useClinic } from '../../../components/ClinicProvider';
import { useTranslation } from 'react-i18next';


interface InventoryApprovalFilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: ApprovalFilterValues) => void;
  onClear: () => void;
  initialValues?: ApprovalFilterValues;
}


const InventoryApprovalFilterModal: React.FC<InventoryApprovalFilterModalProps> = ({
  open,
  onClose,
  onApply,
  onClear,
  initialValues = APPROVAL_FILTER_INITIAL_VALUES,
}) => {

  const { ClinicId } = useClinic();
  const { t: tInventoryApproval } = useTranslation('inventory_approval_config');
  const [clinicList, setClinicList] = useState<Clinic[]>([]);
  const { data: documentTypeOptions = [], isLoading: docTypesLoading, isError: docTypesError } = useDocumentTypes();


  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr) as UserClinicDepartments;
      const mapped = mapUserClinicsResponse(user);
      setClinicList(mapped);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error parsing user from localStorage:', err);
      setClinicList([]);
    }
  }, [ClinicId]);



  return (
    <Modal
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
    >
      <Box sx={filterModalContainerSx}>
        {/* ── Header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          px={2}
          mb={2}
          borderBottom="1px solid rgba(226, 227, 229, 1)"
        >
          <Typography sx={modalHeaderSx}>{tInventoryApproval('filters')}</Typography>
          <Tooltip title={tInventoryApproval('close')} arrow>
            <IconButton onClick={onClose} sx={modalCloseIconSx}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Form ── */}
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values) => {
            onApply(values);
            onClose();
          }}
        >
          {({ values, handleChange, resetForm , setFieldValue}) => (
            <Form>
              <Box p={3} pt={1}>
                {/* Row 1 — Document Type + Clinic */}
                <Grid2 container spacing={2} mb={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                      options={documentTypeOptions}
                      value={documentTypeOptions.find((o) => o.id === values.documentType) ?? null}
                      onChange={(_, newValue) => {
                        setFieldValue('documentType', newValue?.id ?? null);
                      }}
                      getOptionLabel={(o) => o.name}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      loading={docTypesLoading}
                      disabled={docTypesError}
                      ListboxProps={{ sx: { ...autocompleteSx, ...filterInputSubLabelSx } }}
                      data-testid="approval-filter-document-type"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={tInventoryApproval('document_type')}
                          size="small"
                          sx={filterInputSubLabelSx}
                          error={docTypesError}
                          helperText={docTypesError ? tInventoryApproval('doc_type_load_error') : undefined}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {docTypesLoading ? <CircularProgress size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                      options={clinicList}
                      value={clinicList.find((o) => o.id === values.clinicId) ?? null}
                      onChange={(_, newValue) => {
                        setFieldValue('clinicId', newValue?.id ?? null);
                      }}
                      getOptionLabel={(o) => o.name_label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      ListboxProps={{ sx: { ...autocompleteSx, ...filterInputSubLabelSx } }}
                      data-testid="approval-filter-clinic"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={tInventoryApproval('clinic')}
                          size="small"
                          sx={filterInputSubLabelSx}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>

                {/* Row 2 — Status */}
                <Box mb={2}>
                  <Autocomplete
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find((o) => o.value === values.status) ?? null}
                    onChange={(_, newValue) =>
                      handleChange({ target: { name: 'status', value: newValue?.value ?? '' } })
                    }
                    getOptionLabel={(o) => tInventoryApproval(o.value === 'ACTIVE' ? 'active' : 'inactive')}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    ListboxProps={{ sx: { ...autocompleteSx, ...filterInputSubLabelSx } }}
                    data-testid="approval-filter-status"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={tInventoryApproval('status')}
                        size="small"
                        sx={filterInputSubLabelSx}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* ── Footer ── */}
              <Box display="flex" gap={2} p={3} pt={0}>
                <Button
                  variant="outlined"
                  sx={modalCloseSx}
                  data-testid="approval-filter-clear-btn"
                  onClick={() => {
                    resetForm({ values: APPROVAL_FILTER_INITIAL_VALUES });
                    onClear();
                  }}
                >
                  {tInventoryApproval('clear_all')}
                </Button>
                <Button variant="contained" type="submit" sx={modalSaveSx} data-testid="approval-filter-apply-btn">
                  {tInventoryApproval('apply')}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default InventoryApprovalFilterModal;
